if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      // Filters toggle (desktop)
      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        if (this.filterToggle) {
          this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
        }
      }

      // Layout switcher
      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        // Set the layout to the layout in localstorage
        const layout = theme?.storageUtil?.get?.('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      // Compare toggle — fully optional
      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme?.storageUtil?.get?.('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, !!this.compareToggle.checked, false);
        }

        // Add transition classnames safely
        if (this.productsGrid) {
          setTimeout(() => {
            this.classList.add('transition-ready');
            this.productsGrid.classList.add('transition-ready');
          }, 500);
        }
      } else if (theme?.settings?.compareToggle === 'none') {
        // If theme setting says "none", ensure we put UI into a safe "off" state without assuming DOM
        this.toggleCompare(null, true, false);
      }

      // Sorting handler (collection page without facet-filters managing sort)
      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt?.target ? !!evt.target.checked : !!checked;

      // Guard: productsGrid may not exist on some templates
      if (this.productsGrid) {
        this.productsGrid.dataset.compare = isChecked;
      }

      // Guard: compare drawer button may not exist if feature is disabled
      const compareButton = document.getElementById('compare-drawer-open');
      if (compareButton && compareButton.classList) {
        compareButton.classList.toggle('is-out', !isChecked);
      }

      // Keep toggle in sync if it exists
      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      // Persist safely (if storage util is present and not in design mode)
      if (persist && !window.Shopify?.designMode && theme?.storageUtil?.set) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load resources only when enabling, and only if resources tag exists
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          try {
            const resources = JSON.parse(resourcesTag.textContent || '[]');
            resources.forEach((resource) => {
              if (resource.type === 'js' && resource.path) {
                const script = document.createElement('script');
                script.src = resource.path;
                document.head.appendChild(script);
              } else if (resource.type === 'css' && resource.path) {
                const link = document.createElement('link');
                link.href = resource.path;
                link.type = 'text/css';
                link.rel = 'stylesheet';
                document.head.appendChild(link);
              }
            });
          } catch (e) {
            console.error('[ProductsToolbar] Failed to load compare resources', e);
          } finally {
            resourcesTag.remove();
          }
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      if (!this.filterToggle) return;

      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', String(this.filtersOpen));

      if (theme?.mediaMatches?.lg) {
        if (this.filtersColumn) {
          this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        }
        const productsList = document.querySelector('.main-products-grid__results > .grid');
        if (productsList) {
          productsList.className = this.filtersOpen
            ? (productsList.dataset.filtersOpenClasses || productsList.className)
            : (productsList.dataset.filtersClosedClasses || productsList.className);
        }
      } else if (this.filtersComponent?.open) {
        this.filtersComponent.open();
      }
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      if (!this.productsGrid) return;
      const nextLayout = layout || evt?.target?.value;
      if (!nextLayout) return;
      this.productsGrid.dataset.layout = nextLayout;
      if (!layout && theme?.storageUtil?.set) {
        theme.storageUtil.set('products-layout', nextLayout);
      }
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const selectedValue = evt?.detail?.selectedValue ?? evt?.target?.value;
      if (!selectedValue) return;

      try {
        const urlObj = new URL(window.location.href);
        urlObj.searchParams.set('sort_by', selectedValue);
        // Avoid stale pagination when changing sort
        urlObj.searchParams.delete('page');
        urlObj.hash = 'products-toolbar';
        window.location.href = urlObj.toString();
      } catch (e) {
        console.error('[ProductsToolbar] Sort change failed', e);
      }
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
